import type { HostToolResult } from 'models'
import {
    PAGE_NAVIGATION_KEY,
    type HostToolDeferredResult,
    type RegisteredHostTool,
} from 'composables'
import { bringIntoView, fill } from './actions'
import { pageChangeSettled, startPageChange } from './navigation'
import { filteredText, isExcluded, type PageRules } from './snapshot'

/**
 * Forms the site annotated for agents, WebMCP's declarative API (spec
 * `page-tools.md`, "Moduli annotati"): `<form toolname tooldescription>`
 * becomes a tool, its named fields the tool's input.
 */

/** The ceiling of the request schema: more annotated forms are ignored. */
const MAX_FORMS = 16

const FIELD_TYPES_SKIPPED = new Set([
    'submit',
    'button',
    'reset',
    'image',
    'file',
])

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

type Translate = (key: string, params?: Record<string, unknown>) => string

const text = (value: string): HostToolResult => ({
    content: [{ type: 'text', text: value }],
})

/**
 * `form_` and the site's name in our alphabet, within the 64 characters of a
 * tool name: `Book a table!` becomes `form_book_a_table`.
 */
export function formToolName(toolname: string): string | undefined {
    const normalized = toolname
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 59)
    return normalized ? `form_${normalized}` : undefined
}

const isField = (element: Element): element is Field =>
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement

/**
 * The fields the agent may fill, by name. Radios of one name are one field;
 * of other fields sharing a name, the first is the one filled. Disabled,
 * excluded and sensitive fields are left out, so their name never reaches the
 * model either.
 */
function fieldsOf(form: HTMLFormElement, rules: PageRules) {
    const fields = new Map<string, Field[]>()
    for (const element of Array.from(form.elements)) {
        if (
            !isField(element) ||
            !element.name ||
            element.disabled ||
            (element instanceof HTMLInputElement &&
                FIELD_TYPES_SKIPPED.has(element.type)) ||
            isExcluded(element, rules)
        ) {
            continue
        }
        const group = fields.get(element.name)
        if (!group) {
            fields.set(element.name, [element])
        } else if (
            element instanceof HTMLInputElement &&
            element.type === 'radio'
        ) {
            group.push(element)
        }
    }
    return fields
}

/** As WebMCP: `toolparamdescription`, then the label, then `aria-description`. */
function describeField(field: Field, rules: PageRules): string | undefined {
    const own = field.getAttribute('toolparamdescription')
    if (own) {
        return own
    }
    const labels = [...(field.labels ?? [])]
        .map((label) => filteredText(label, rules, 200))
        .filter(Boolean)
    if (labels.length > 0) {
        return labels.join(' ')
    }
    return field.getAttribute('aria-description') ?? undefined
}

const optionLabel = (option: HTMLOptionElement) =>
    option.textContent?.trim() || option.value

function propertyOf(fields: Field[], rules: PageRules) {
    const [first] = fields
    const description = describeField(first, rules)
    if (first instanceof HTMLSelectElement) {
        const options = [...first.options].filter(
            (option) => option.value !== '' && !isExcluded(option, rules),
        )
        const labelled = options.some(
            (option) => optionLabel(option) !== option.value,
        )
        return {
            type: 'string',
            enum: options.map(({ value }) => value),
            ...((description || labelled) && {
                description: [
                    description,
                    labelled &&
                        `Options: ${options.map((option) => `${option.value} = ${optionLabel(option)}`).join(', ')}`,
                ]
                    .filter(Boolean)
                    .join('. '),
            }),
        }
    }
    const base = description ? { description } : {}
    if (first instanceof HTMLInputElement && first.type === 'radio') {
        return {
            ...base,
            type: 'string',
            enum: fields.map(({ value }) => value),
        }
    }
    if (first instanceof HTMLInputElement && first.type === 'checkbox') {
        return { ...base, type: 'boolean' }
    }
    if (
        first instanceof HTMLInputElement &&
        (first.type === 'number' || first.type === 'range')
    ) {
        return { ...base, type: 'number' }
    }
    return { ...base, type: 'string' }
}

function inputSchemaOf(form: HTMLFormElement, rules: PageRules) {
    const properties: Record<string, unknown> = {}
    const required: string[] = []
    for (const [name, fields] of fieldsOf(form, rules)) {
        properties[name] = propertyOf(fields, rules)
        if (fields.some((field) => field.required)) {
            required.push(name)
        }
    }
    return {
        type: 'object',
        properties,
        ...(required.length > 0 && { required }),
    }
}

/** The annotated forms of the page now, by tool name; the first of a name wins. */
function annotatedForms(rules: PageRules) {
    const forms = new Map<string, HTMLFormElement>()
    for (const form of document.querySelectorAll<HTMLFormElement>(
        'form[toolname]',
    )) {
        const name = formToolName(form.getAttribute('toolname') ?? '')
        if (name && !forms.has(name) && !isExcluded(form, rules)) {
            forms.set(name, form)
        }
        if (forms.size >= MAX_FORMS) {
            break
        }
    }
    return forms
}

/** What the call writes into the form: the outcome of each field, by name. */
function fillForm(
    form: HTMLFormElement,
    input: unknown,
    rules: PageRules,
): string[] {
    const fields = fieldsOf(form, rules)
    const outcomes: string[] = []
    for (const [name, value] of Object.entries(
        (input ?? {}) as Record<string, unknown>,
    )) {
        const group = fields.get(name)
        if (!group) {
            outcomes.push(`${name}: not a field of this form`)
            continue
        }
        const [first] = group
        const isRadio =
            first instanceof HTMLInputElement && first.type === 'radio'
        const target = isRadio
            ? group.find((radio) => radio.value === String(value))
            : first
        if (!target) {
            throw new Error(
                `No option "${String(value)}" for ${name}: the options are ${group.map((radio) => radio.value).join(', ')}`,
            )
        }
        // A radio is checked by picking it, whatever value it carries.
        outcomes.push(`${name}: ${fill(target, isRadio ? true : value, rules)}`)
    }
    return outcomes
}

/**
 * Submits as WebMCP's `toolautosubmit` does: `requestSubmit()`, with the
 * `submit` event carrying `agentInvoked` and `respondWith(promise)`. A site
 * that calls `respondWith` after `preventDefault()` answers the model with
 * the promise's value. One that lets the browser submit would unload the
 * page before the answer is out: that submit is held, and made again once the
 * answer reached the backend, like a click that leaves the page.
 */
async function submitForm(
    form: HTMLFormElement,
    signal: AbortSignal,
): Promise<HostToolResult | HostToolDeferredResult> {
    let submitted = false
    let response: Promise<unknown> | undefined
    let sitePrevented = false
    // On the window, in the capture phase: the first listener the submit
    // meets, so a site's `stopPropagation` cannot keep it from running. It
    // always holds the browser's own submit, which would unload the page
    // before the answer is out, and notes whether the site stopped it too.
    // The two WebMCP properties are defined on the event itself: Chrome has
    // read-only ones on `SubmitEvent.prototype`, and assigning them throws
    // inside this listener, where the error goes unseen (measured on Chrome
    // 155: the site read `agentInvoked` false).
    const mark = (event: Event) => {
        if (event.target !== form) {
            return
        }
        submitted = true
        const preventDefault = event.preventDefault.bind(event)
        Object.defineProperties(event, {
            agentInvoked: { value: true, configurable: true },
            respondWith: {
                value: (value: unknown) => {
                    response = Promise.resolve(value)
                },
                configurable: true,
            },
            preventDefault: {
                value: () => {
                    sitePrevented = true
                    preventDefault()
                },
                configurable: true,
            },
        })
        preventDefault()
    }
    window.addEventListener('submit', mark, { capture: true })
    try {
        form.requestSubmit()
    } finally {
        window.removeEventListener('submit', mark, { capture: true })
    }
    const held = !response && !sitePrevented

    if (!submitted) {
        const invalid = form.querySelector<Field>(':invalid')
        throw new Error(
            `The form did not pass its own checks${invalid ? `: ${invalid.name || 'a field'}, ${invalid.validationMessage}` : ''}. Fix the input and call the tool again.`,
        )
    }
    if (response) {
        const aborted = new Promise<never>((_, reject) =>
            signal.addEventListener('abort', () => reject(signal.reason), {
                once: true,
            }),
        )
        const value = await Promise.race([response, aborted])
        const answer =
            typeof value === 'string' ? value : JSON.stringify(value ?? null)
        return text(`The form was submitted. The site answered: ${answer}`)
    }
    if (held) {
        return {
            result: text(
                'Submitting the form. The page may change; if it does, the conversation goes on on the new page, so read it with page_snapshot before doing anything else.',
            ),
            afterOutput: () => {
                try {
                    sessionStorage.setItem(
                        PAGE_NAVIGATION_KEY,
                        String(Date.now()),
                    )
                } catch {
                    // Without storage the new page cannot pick the answer up.
                }
                startPageChange()
                // The native submit fires no second `submit` event.
                HTMLFormElement.prototype.submit.call(form)
            },
        }
    }
    return text(
        'The form was submitted and the site handled it on the page: read the page with page_snapshot to see the outcome.',
    )
}

/**
 * The tools of the forms annotated on the page now. Called on every request
 * and before every call, since a page adds and removes forms as it goes.
 */
export function discoverFormTools(
    rules: PageRules,
    { t, confirmEveryAction }: { t: Translate; confirmEveryAction: boolean },
): RegisteredHostTool[] {
    const tools: RegisteredHostTool[] = []
    for (const [name, form] of annotatedForms(rules)) {
        const autoSubmit = form.hasAttribute('toolautosubmit')
        const label =
            form.getAttribute('tooldescription') ||
            form.getAttribute('toolname') ||
            name
        // The site's `data-agent-confirm`, on the form or on a field the call
        // writes: such a field may save itself on `change`, without a submit.
        const protectedBySite = (input: unknown) => {
            if (form.closest('[data-agent-confirm]')) {
                return true
            }
            const fields = fieldsOf(form, rules)
            return Object.keys((input ?? {}) as Record<string, unknown>).some(
                (key) =>
                    fields
                        .get(key)
                        ?.some((field) =>
                            field.closest('[data-agent-confirm]'),
                        ),
            )
        }
        tools.push({
            name,
            description: form.getAttribute('tooldescription') ?? undefined,
            inputSchema: inputSchemaOf(form, rules),
            annotations: { title: t('messagePart.pageTool.form') },
            policy: { timeoutMs: 30_000 },
            // A submit always asks first, as a click on a submit button does.
            confirmation: (input) =>
                autoSubmit || confirmEveryAction || protectedBySite(input)
                    ? t(
                          autoSubmit
                              ? 'toolApproval.formSubmit'
                              : 'toolApproval.formFill',
                          { name: label },
                      )
                    : undefined,
            async execute(input, { signal }) {
                await pageChangeSettled()
                // Found again: the page may have replaced the form since the
                // request that declared it.
                const current = annotatedForms(rules).get(name)
                if (!current) {
                    throw new Error(
                        'That form is no longer on the page: read it again with page_snapshot.',
                    )
                }
                bringIntoView(current)
                const outcomes = fillForm(current, input, rules)
                // Decided with the confirmation, not read again: a script of
                // the page adding `toolautosubmit` while the fields fill in
                // must not get a submit the user was never asked about.
                if (!autoSubmit || !current.hasAttribute('toolautosubmit')) {
                    current
                        .querySelector<HTMLElement>(
                            'button[type=submit], button:not([type]), input[type=submit]',
                        )
                        ?.focus()
                    return text(
                        `${outcomes.join('; ')}. The form waits for the user to submit it: ask them to check it and press its button.`,
                    )
                }
                const submitted = await submitForm(current, signal)
                const prefix = `${outcomes.join('; ')}. `
                return 'afterOutput' in submitted
                    ? {
                          ...submitted,
                          result: text(
                              prefix + submitted.result.content[0].text,
                          ),
                      }
                    : text(prefix + submitted.content[0].text)
            },
        })
    }
    return tools
}
